import React, { useEffect, useState } from 'react';
import { X, LoaderCircle } from 'lucide-react';
import { Button, Tab } from 'common/components';
import { Contact } from 'types/contact.type';
import { useGetContactDetails } from 'services/hooks/mutations/useGetContactDetails';
import { calculateDuration, processStatusHistory, ProcessedStatusHistory } from './ContactManager.utils';
import { TabDetails, TabChangeStatus } from './components';
import { CallDirectionType, DispositionType } from 'types/call.type';
import { SMSDirectionType } from 'types/sms.type';

import styles from './ContactManager.module.scss';

export type ProcessedCallHistory = {
    id: string;
    date: Date;
    duration: string;
    status: string;
    workflow: string;
    callDirection?: CallDirectionType;
    disposition?: DispositionType;
    type: 'call';
};

export type ProcessedSMSHistory = {
    id: string;
    date: Date;
    message: string;
    status: string;
    workflow: string;
    messageDirection?: SMSDirectionType;
    disposition?: DispositionType;
    type: 'sms';
};

/** Discriminated union — interfaces cannot extend `A | B`. */
export type ProcessedHistory = ProcessedCallHistory | ProcessedSMSHistory;

interface ContactDrawerProps {
    isOpen: boolean;
    contact: Contact | null;
    activeTab: string;
    rawStatusOptions: string[];
    onClose: () => void;
    onTabChange: (tabId: string) => void;
}

const ContactDrawer: React.FC<ContactDrawerProps> = ({
    isOpen,
    contact,
    activeTab,
    rawStatusOptions,
    onClose,
    onTabChange,
}) => {
    const [contactHistory, setContactHistory] = useState<ProcessedHistory[]>([]);
    const [statusHistory, setStatusHistory] = useState<ProcessedStatusHistory[]>([]);
    const { contactDetails, isContactDetailsLoading } = useGetContactDetails({
        id: contact?.contactId,
        isEnabled: !!isOpen && !!contact?.contactId,
    });

    useEffect(() => {
        if (!contactDetails) return;

        const formattedCalls =
            contactDetails?.calls
                ?.map((call) => {
                    return {
                        id: call.id,
                        date: new Date(call.start_time || call.created_at),
                        duration: calculateDuration(call.start_time, call.end_time),
                        status: call.status || 'Unknown',
                        workflow: call.edges && call.edges.workflow ? call.edges.workflow.title : 'Unknown',
                        callDirection: call.call_direction,
                        disposition: call.disposition,
                        type: 'call' as const,
                    };
                })
                .sort((a, b) => b.date.getTime() - a.date.getTime()) || [];

        const formatedSMS =
            contactDetails?.sms?.map((sms) => {
                return {
                    id: sms.smsId,
                    date: new Date(sms.sentTime),
                    message: sms.messageContent,
                    status: sms.status || 'Unknown',
                    workflow: sms.workflowName,
                    messageDirection: sms.messageDirection,
                    disposition: sms.disposition,
                    type: 'sms' as const,
                };
            }) || [];

        const formattedHistory = [...formattedCalls, ...formatedSMS].sort(
            (a, b) => b.date.getTime() - a.date.getTime()
        );

        setContactHistory(formattedHistory);

        setStatusHistory(processStatusHistory(contactDetails.history));

        return () => {
            setContactHistory([]);
            setStatusHistory([]);
        };
    }, [contactDetails]);

    // Format date to display in a readable format
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    // Format time from date object
    const formatTime = (date: Date): string => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            {isOpen && <div className={styles.drawerOverlay} onClick={onClose}></div>}
            <div className={`${styles.drawer} ${isOpen ? styles.open : ''}`}>
                <div className={styles.drawerHeader}>
                    <h2>{contact?.contactName}</h2>
                    <Button
                        variant='ghost'
                        size='sm'
                        icon={X}
                        iconOnly
                        onClick={onClose}
                        className={styles.drawerCloseButton}
                        aria-label='Close drawer'
                    />
                </div>
                <div className={styles.drawerTabs}>
                    <Tab
                        variant='buttonStyle'
                        tabs={[
                            { id: 'details', label: 'Contact History' },
                            { id: 'history', label: 'Status History' },
                            { id: 'change', label: 'Change Status' },
                        ]}
                        activeTab={activeTab}
                        onTabChange={onTabChange}
                    />
                </div>
                <div className={styles.drawerContent}>
                    {isContactDetailsLoading && (
                        <div className={styles.loadingOverlay}>
                            <LoaderCircle size={26} className={styles.spinner} color='var(--primary-purple)' /> Fetching
                            contact details...
                        </div>
                    )}
                    {activeTab === 'details' && (
                        <TabDetails contactHistory={contactHistory} contactName={contact?.contactName} />
                    )}

                    {activeTab === 'history' && (
                        <div>
                            <h3>Status Change History</h3>
                            <div className={styles.callsTimeline}>
                                {statusHistory.length > 0 ? (
                                    statusHistory.map((item, index) => (
                                        <div className={styles.timelineItem} key={item.id || index}>
                                            <div className={styles.timelineDate}>
                                                {formatDate(item.date.toISOString())}
                                                <div className={styles.callTime}>{formatTime(item.date)}</div>
                                            </div>

                                            <div className={styles.timelineContent}>
                                                <div className={styles.timelineHeader}>
                                                    Status changed to <strong>{item.status}</strong>
                                                </div>
                                                {item.note && <div className='mt-xs'>Note: {item.note}</div>}
                                                <div className={`${styles.timelineUser} mt-sm`}>
                                                    by {item.changedBy}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className={styles.noCallsMessage}>
                                        No status history available for this contact.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'change' && contact && (
                        <TabChangeStatus rawStatusOptions={rawStatusOptions} contact={contact} />
                    )}
                </div>
            </div>
        </>
    );
};

export default ContactDrawer;
